/**
 * Carril [A] · PR2 — Contenido de ayuda in-app (tour progresivo + FAQ), en UN solo
 * mapa editable por vertical. El asesor/Arturo PULEN el copy aquí (no hay texto de
 * ayuda hardcodeado disperso). Cada entrada alimenta dos cosas:
 *   - `coach`: el coach-mark que aparece la 1ª vez que el usuario entra a la sección
 *     (tour progresivo) y cada paso del tour completo re-lanzable.
 *   - `faq`:  los pasos "cómo se usa" (click-paths reales de HOY) del Centro de ayuda.
 *
 * `key` = id de la vista/nav (`active` en el Shell) → el coach-mark se ancla a
 * `[data-tour="nav-<key>"]` (el item del sidebar, siempre montado si aplica). Si el
 * ancla no existe (permiso/módulo), el coach simplemente no aparece (no rompe).
 *
 * ▸ DRAFT: el copy es un borrador redactado desde las pantallas reales. Marcas
 *   `draft: true` = pendiente de que el asesor lo pula. Cambiar texto aquí NO
 *   requiere tocar componentes.
 */
export type HelpModule = 'crm' | 'gastro' | 'gym' | 'boutique';

export interface HelpMe {
  role?: string;
  permissions?: string[];
  tenant?: { enabledModules?: string[] };
}

export interface HelpEntry {
  /** id de la vista / nav (`active`). Ancla del coach: [data-tour="nav-<key>"]. */
  key: string;
  /** Vertical al que pertenece (para agrupar el FAQ y filtrar). null = transversal. */
  vertical: HelpModule | null;
  title: string;
  /** Coach-mark: 2–4 frases — qué es, para qué sirve y qué puedes hacer aquí. */
  coach: string;
  /** FAQ: pasos concretos (click-paths de hoy). El asesor pule/expande. */
  faq: string[];
  /** Se muestra solo si aplica al tenant (módulo × permiso). */
  show: (me: HelpMe) => boolean;
  /** true = copy borrador, pendiente de pulir por el asesor. */
  draft?: boolean;
}

const hasPerm = (me: HelpMe, p: string): boolean => {
  if (!me) return false;
  if (me.role === 'SUPER_USER') return true;
  return Array.isArray(me.permissions) && me.permissions.includes(p);
};
export const helpHasModule = (me: HelpMe, m: string): boolean => {
  const mods = me?.tenant?.enabledModules?.length ? me.tenant.enabledModules : ['crm', 'boutique', 'gastro'];
  return mods.includes(m);
};

/** Etiqueta legible de cada vertical (encabezados del Centro de ayuda). */
export const VERTICAL_LABEL: Record<string, string> = {
  gastro: 'GastroGest', gym: 'Wellness', boutique: 'Boutique', crm: 'Flow', transversal: 'General',
};

// ---------------------------------------------------------------------------
// Contenido. Orden dentro de cada vertical = orden del tour (empieza por lo suyo).
// ---------------------------------------------------------------------------
export const HELP_CONTENT: HelpEntry[] = [
  // ===== GastroGest =====
  { key: 'gastro-pos', vertical: 'gastro', title: 'Mesas y comanda',
    coach: 'El corazón del restaurante: abre una mesa (o una venta rápida), arma la comanda con platillos y modificadores, y cobra en efectivo, tarjeta o transferencia. En efectivo verás el cambio en vivo y, si lo activaste, el redondeo a favor del cliente.',
    faq: [
      'Abre una mesa o usa "Venta rápida" para llevar/mostrador.',
      'Toca un producto para agregarlo; elige modificadores/variantes si los pide.',
      'Cobra: elige método; en efectivo captura lo recibido y el sistema calcula el cambio.',
      'Al cerrar, la venta descuenta inventario e insumos y alimenta el Cierre de día.',
    ],
    show: (me) => helpHasModule(me, 'gastro') },
  { key: 'gastro-products', vertical: 'gastro', title: 'Productos (menú)',
    coach: 'Tu menú: cada platillo con precio, costo, IVA y control de inventario opcional. De aquí salen los productos que aparecen en la comanda.',
    faq: [
      'Nuevo producto: nombre, precio y (opcional) costo para ver margen.',
      'Activa "controlar inventario" si quieres que la venta descuente stock.',
      'Filtra por categoría o nombre con el buscador de la vista.',
    ],
    show: (me) => helpHasModule(me, 'gastro') },
  { key: 'gastro-insumos', vertical: 'gastro', title: 'Insumos',
    coach: 'Los ingredientes detrás de tus platillos. Controla existencias a nivel insumo y define un mínimo para recibir alertas de reabasto.',
    faq: [
      'Da de alta cada insumo con su unidad (kg, L, pieza) y stock actual.',
      'Fija el "mínimo" para que Inteligencia de Inventario te avise antes de quedarte sin él.',
      'Los insumos se descuentan al vender platillos que tengan receta.',
    ],
    show: (me) => helpHasModule(me, 'gastro') },
  { key: 'gastro-modificadores', vertical: 'gastro', title: 'Modificadores / arma tu platillo',
    coach: 'Grupos de opciones reutilizables (término, extras, sin cebolla…) que enriquecen un platillo y pueden sumar costo. Aquí también atas insumos a una opción para que descuente inventario.',
    faq: [
      'Crea un grupo (ej. "Término") y sus opciones; marca si es obligatorio u opcional.',
      'Asigna costo por opción para que el margen sea real.',
      'Asócialo a los platillos que lo usen; en el POS el cajero lo elige al vender.',
    ],
    show: (me) => helpHasModule(me, 'gastro') },
  { key: 'gastro-paquetes', vertical: 'gastro', title: 'Paquetes (menú por tiempos)',
    coach: 'Menús de precio fijo por tiempos (entrada + plato + postre). El comensal elige dentro de cada tiempo y se cobra un precio único.',
    faq: [
      'Crea el paquete con su precio fijo.',
      'Define los tiempos y qué platillos puede elegir el comensal en cada uno.',
      'En el POS se agrega como un solo ítem con las elecciones del comensal.',
    ],
    show: (me) => helpHasModule(me, 'gastro'), draft: true },
  { key: 'gastro-corte', vertical: 'gastro', title: 'Cierre de día',
    coach: 'El corte de caja: ventas del día (o turno) desglosadas por método de pago, propinas y órdenes. En efectivo muestra además el efectivo esperado en el cajón para cuadrar el arqueo. Imprime el ticket de corte.',
    faq: [
      'Elige el día y, opcional, el rango de horas del turno.',
      'Revisa el desglose por método y el efectivo esperado en caja.',
      'Imprime el corte con "Imprimir ticket".',
    ],
    show: (me) => helpHasModule(me, 'gastro') },

  // ===== Wellness (gym) =====
  { key: 'gym-plans', vertical: 'gym', title: 'Planes y membresías',
    coach: 'Define tus membresías y paquetes de clases: precio, vigencia y tipo de acceso. Es la base de lo que vendes a tus miembros.',
    faq: [
      'Crea un plan con nombre, precio y duración (mensual, por visitas, etc.).',
      'Define el tipo de acceso que otorga (clases, gimnasio libre…).',
      'Los planes se venden y renuevan desde "Miembros".',
    ],
    show: (me) => helpHasModule(me, 'gym') },
  { key: 'gym-members', vertical: 'gym', title: 'Miembros',
    coach: 'Tu padrón: da de alta, renueva o cambia el plan de cada miembro y consulta su vigencia. Aquí se vende la membresía.',
    faq: [
      'Alta de miembro: sus datos + el plan que contrata.',
      'Renueva o cambia de plan cuando venza o el miembro lo pida.',
      'Consulta la vigencia antes de darle acceso.',
    ],
    show: (me) => helpHasModule(me, 'gym') },
  { key: 'gym-classes', vertical: 'gym', title: 'Clases y horarios',
    coach: 'El calendario de clases con cupo y reservas. Arma horarios (incluso recurrentes), asigna instructor y aula, y controla la lista de espera.',
    faq: [
      'Crea una clase: día/hora, cupo, instructor y aula.',
      'Para horarios repetidos usa la recurrencia (elige días + fecha fin).',
      'Activa el canal público (link/QR) en Configuración para que reserven en línea.',
    ],
    show: (me) => helpHasModule(me, 'gym') },
  { key: 'gym-checkin', vertical: 'gym', title: 'Check-in',
    coach: 'Valida el acceso del miembro al llegar: confirma su vigencia y registra la visita según el tipo de acceso.',
    faq: [
      'Busca al miembro y confirma que su plan esté vigente.',
      'Elige el tipo de acceso y registra el check-in.',
      'Las visitas alimentan los reportes de asistencia.',
    ],
    show: (me) => helpHasModule(me, 'gym') },
  { key: 'gym-corte', vertical: 'gym', title: 'Cierre de día',
    coach: 'Corte de caja del día/turno por método de pago, con propinas y efectivo esperado en el cajón para cuadrar el arqueo.',
    faq: [
      'Elige el día y, opcional, el turno.',
      'Revisa el desglose por método y el efectivo esperado.',
      'Imprime el corte con "Imprimir ticket".',
    ],
    show: (me) => helpHasModule(me, 'gym') },

  // ===== Boutique =====
  { key: 'boutique-agenda', vertical: 'boutique', title: 'Agenda',
    coach: 'Tu calendario de citas: programa servicios por estación/profesional y revisa el día. Las reservas en línea aparecen aquí también.',
    faq: [
      'Crea una cita: cliente, servicio, fecha/hora y profesional.',
      'Revisa el día en la vista de calendario.',
      'Activa el canal público en Configuración para recibir reservas en línea.',
    ],
    show: (me) => helpHasModule(me, 'boutique') },
  { key: 'boutique-services', vertical: 'boutique', title: 'Servicios',
    coach: 'Tu catálogo de servicios con precio, costo e IVA. Es lo que agendas y cobras; combínalo con productos en la venta.',
    faq: [
      'Nuevo servicio: nombre, precio y duración.',
      'Agrega costo para ver el margen.',
      'Filtra por categoría o nombre con el buscador.',
    ],
    show: (me) => helpHasModule(me, 'boutique') },
  { key: 'boutique-products', vertical: 'boutique', title: 'Productos e inventario',
    coach: 'Los productos que vendes junto al servicio (retail). Controla stock y súmalos a la venta en el mismo ticket.',
    faq: [
      'Alta de producto con precio y (opcional) control de inventario.',
      'La venta descuenta stock si lo activaste.',
      'Agrega productos al cobro junto con el servicio.',
    ],
    show: (me) => helpHasModule(me, 'boutique') },
  { key: 'boutique-reminders', vertical: 'boutique', title: 'Recordatorios',
    coach: 'Da seguimiento a tus clientes (reagenda, cumpleaños, recompra) por WhatsApp o email para que vuelvan.',
    faq: [
      'Revisa las sugerencias de seguimiento del día.',
      'Envía el recordatorio por WhatsApp o email.',
      'Descarta o pospón los que no apliquen.',
    ],
    show: (me) => helpHasModule(me, 'boutique') },
  { key: 'boutique-corte', vertical: 'boutique', title: 'Cierre de día',
    coach: 'Corte de caja del día/turno por método de pago, con propinas y efectivo esperado en el cajón para cuadrar.',
    faq: [
      'Elige el día y, opcional, el turno.',
      'Revisa el desglose por método y el efectivo esperado.',
      'Imprime el corte con "Imprimir ticket".',
    ],
    show: (me) => helpHasModule(me, 'boutique') },

  // ===== Flow (crm) =====
  { key: 'contacts', vertical: 'crm', title: 'Contactos',
    coach: 'Tu directorio de clientes y prospectos, compartido por todos los módulos. Desde aquí abres el perfil 360 de cada cliente y su historial.',
    faq: [
      'Agrega un contacto o impórtalos en lote.',
      'Abre el perfil para ver su historial y actividades.',
      'Úsalo como base para cotizar, agendar o comunicar.',
    ],
    // Compartido, pero "solo si aplica" (design doc): en el tour/FAQ lo mostramos
    // para Flow (su paso de entrada); otras verticales no lo listan aquí.
    show: (me) => helpHasModule(me, 'crm') },
  { key: 'pipeline', vertical: 'crm', title: 'Pipeline de ventas',
    coach: 'Tus oportunidades en un tablero: arrástralas entre etapas para mover la venta. El forecast pondera cada oportunidad por su probabilidad de cierre.',
    faq: [
      'Crea una oportunidad y ubícala en su etapa.',
      'Arrástrala entre etapas conforme avanza.',
      'Configura tus etapas en Configuración › Operación.',
    ],
    show: (me) => helpHasModule(me, 'crm') },
  { key: 'projects', vertical: 'crm', title: 'Proyectos y horas',
    coach: 'Da seguimiento a entregables y registra las horas del equipo por tipo de actividad. Las horas facturables alimentan el margen del proyecto.',
    faq: [
      'Crea un proyecto y sus tareas/entregables.',
      'Registra horas y marca si son facturables.',
      'Define los tipos de actividad en Configuración › Operación.',
    ],
    show: (me) => helpHasModule(me, 'crm') },
  { key: 'quotes', vertical: 'crm', title: 'Cotizaciones',
    coach: 'Crea y envía cotizaciones profesionales; al aceptarse se convierten en proyecto. Aquí vive el flujo comercial de Flow.',
    faq: [
      'Nueva cotización: cliente, conceptos y precios.',
      'Envíala; el cliente la revisa y acepta.',
      'Al aceptarse puede convertirse en proyecto para dar seguimiento.',
    ],
    show: (me) => helpHasModule(me, 'crm'), draft: true },

  // ===== Transversal =====
  { key: 'finance-pnl', vertical: null, title: 'Ingresos vs gastos',
    coach: 'La foto financiera del dueño: ingresos − gastos = utilidad del periodo. Registra tus gastos en la sección de al lado para que el número sea real.',
    faq: [
      'Registra tus egresos en "Gastos".',
      'Revisa aquí ingresos − gastos por periodo.',
      'Es un dato del dueño (requiere permiso financiero).',
    ],
    show: (me) => hasPerm(me, 'financials:view') },
  { key: 'settings', vertical: null, title: 'Configuración',
    coach: 'Todo el setup del negocio, agrupado: marca, sucursales, facturación (CFDI), cobros y pagos, usuarios y accesos. Empieza por "Negocio y marca". Tu cuenta personal está fija arriba.',
    faq: [
      'Negocio y marca: nombre, logo, color, moneda, plan y sucursales.',
      'Facturación (CFDI) y Cobros y pagos: datos fiscales, Stripe, redondeo de efectivo.',
      'Usuarios, acceso y legal: tu equipo, ventana de sesión y documentos legales.',
      'Usa el buscador de Configuración para encontrar un ajuste rápido.',
    ],
    show: () => true },
];

/** Orden del TOUR completo: la vertical del tenant PRIMERO (no arranca con Flow/
 *  Contactos para un restaurante), luego el resto, y transversal al final. */
const MODULE_PRIORITY: HelpModule[] = ['gastro', 'gym', 'boutique', 'crm'];

const byKey = new Map(HELP_CONTENT.map((e) => [e.key, e]));
export const helpEntry = (key: string): HelpEntry | undefined => byKey.get(key);

/** Entradas aplicables al tenant, en orden de tour (vertical-first + transversal). */
export function tourEntriesFor(me: HelpMe): HelpEntry[] {
  const out: HelpEntry[] = [];
  const push = (e?: HelpEntry) => { if (e && e.show(me) && !out.includes(e)) out.push(e); };
  for (const mod of MODULE_PRIORITY) {
    if (!helpHasModule(me, mod)) continue;
    for (const e of HELP_CONTENT) if (e.vertical === mod) push(e);
  }
  for (const e of HELP_CONTENT) if (e.vertical === null) push(e); // transversal al final
  return out;
}

/** Agrupado por vertical (para el Centro de ayuda / FAQ), solo lo que aplica. */
export function helpGroupsFor(me: HelpMe): Array<{ vertical: string; label: string; entries: HelpEntry[] }> {
  const groups: Array<{ vertical: string; label: string; entries: HelpEntry[] }> = [];
  const order: Array<HelpModule | 'transversal'> = [...MODULE_PRIORITY, 'transversal'];
  for (const v of order) {
    const entries = HELP_CONTENT.filter((e) =>
      (v === 'transversal' ? e.vertical === null : e.vertical === v) && e.show(me));
    if (entries.length) groups.push({ vertical: v, label: VERTICAL_LABEL[v] ?? v, entries });
  }
  return groups;
}
